import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], subtotal: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState(localStorage.getItem("affiliateCode") || "");
  const { token, getAuthHeaders } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!token) {
      setCart({ items: [], subtotal: 0, total: 0 });
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`, {
        headers: getAuthHeaders()
      });
      setCart(response.data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
  }, [token, getAuthHeaders]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    if (!token) {
      toast.error("Please login to add items to cart");
      return false;
    }
    
    try {
      await axios.post(`${API}/cart/add`, 
        { product_id: productId, quantity },
        { headers: getAuthHeaders() }
      );
      await fetchCart();
      toast.success("Added to cart!");
      return true;
    } catch (error) {
      toast.error("Failed to add item to cart");
      return false;
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      await axios.put(`${API}/cart/update`,
        { product_id: productId, quantity },
        { headers: getAuthHeaders() }
      );
      await fetchCart();
    } catch (error) {
      toast.error("Failed to update cart");
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/remove/${productId}`, {
        headers: getAuthHeaders()
      });
      await fetchCart();
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, {
        headers: getAuthHeaders()
      });
      setCart({ items: [], subtotal: 0, total: 0 });
    } catch (error) {
      console.error("Failed to clear cart:", error);
    }
  };

  const setAffiliate = (code) => {
    setAffiliateCode(code);
    if (code) {
      localStorage.setItem("affiliateCode", code);
    } else {
      localStorage.removeItem("affiliateCode");
    }
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      cartCount,
      affiliateCode,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      fetchCart,
      setAffiliate
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
